import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAppState } from "../context/AppState";
import { useLocale } from "../context/Locale";
import { api } from "../lib/api";

const FOOD_THEME_CACHE_KEY = "delivery-huaraz:food-theme:v2";
const FOOD_THEME_IDS = ["current", "visual_categories", "delivery_huaraz_red"];
const DEFAULT_THEME_COLORS = {
  navigationBackground: "#FFFFFF",
  navigationActive: "#D72547",
  navigationInactive: "#171717",
  browserBar: "#FF375B",
};

function safeColor(value, fallback) {
  return /^#[0-9A-F]{6}$/i.test(String(value || "")) ? String(value).toUpperCase() : fallback;
}

function readThemeConfig() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(FOOD_THEME_CACHE_KEY) || "null");
    if (!cached || !FOOD_THEME_IDS.includes(cached.theme)) return null;
    return { theme: cached.theme, colors: { ...DEFAULT_THEME_COLORS, ...(cached.colors || {}) } };
  } catch {
    return null;
  }
}

const items = [
  { to: "/food", icon: "location", label: "nearby" },
  { to: "/alerts", icon: "bell", label: "alerts" },
  { to: "/explore", icon: "search", label: "explore" },
  { to: "/cart", icon: "bag", label: "cart", cart: true },
  { to: "/account", icon: "user", label: "account" },
];

const deliveryHuarazRedItems = [
  { to: "/food", image: "/assets/themes/delivery-huaraz-red/navegacion/inicio.png", label: "home" },
  { to: "/alerts", image: "/assets/themes/delivery-huaraz-red/navegacion/ofertas.png", label: "offers" },
  { to: "/explore", image: "/assets/themes/delivery-huaraz-red/navegacion/explorar.png", label: "explore" },
  { to: "/cart", image: "/assets/themes/delivery-huaraz-red/navegacion/carrito.png", label: "cart", cart: true },
  { to: "/account", image: "/assets/themes/delivery-huaraz-red/navegacion/cuenta.png", label: "account" },
];

function MenuIcon({ name }) {
  const paths = {
    location: (
      <>
        <path d="M12 21s7-6.1 7-12a7 7 0 1 0-14 0c0 5.9 7 12 7 12Z" />
        <circle cx="12" cy="9" r="2.5" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
        <path d="M9.5 21h5" />
      </>
    ),
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m15.5 15.5 5 5" />
      </>
    ),
    bag: (
      <>
        <path d="M5 8h14l1 13H4L5 8Z" />
        <path d="M9 9V6a3 3 0 0 1 6 0v3" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0Z" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

export default function BottomNav({ theme }) {
  const { cartCount } = useAppState();
  const { t } = useLocale();
  const [cartPulse, setCartPulse] = useState(0);
  const [themeConfig, setThemeConfig] = useState(() => readThemeConfig());

  useEffect(() => {
    const pulse = () => setCartPulse((current) => current + 1);
    window.addEventListener("dh:cart-added", pulse);
    return () => window.removeEventListener("dh:cart-added", pulse);
  }, []);

  useEffect(() => {
    let active = true;
    api("/service-hub")
      .then((data) => {
        if (!active) return;
        const nextTheme = FOOD_THEME_IDS.includes(data?.food_home?.theme) ? data.food_home.theme : "current";
        const options = data?.food_home?.theme_options?.delivery_huaraz_red || {};
        const cachedColors = readThemeConfig()?.colors || {};
        const colors = {
          ...cachedColors,
          navigationBackground: safeColor(options.navigation_background, DEFAULT_THEME_COLORS.navigationBackground),
          navigationActive: safeColor(options.navigation_active, DEFAULT_THEME_COLORS.navigationActive),
          navigationInactive: safeColor(options.navigation_inactive, DEFAULT_THEME_COLORS.navigationInactive),
          browserBar: safeColor(options.browser_bar, DEFAULT_THEME_COLORS.browserBar),
        };
        const nextConfig = { theme: nextTheme, colors };
        setThemeConfig(nextConfig);
        try {
          const current = JSON.parse(window.localStorage.getItem(FOOD_THEME_CACHE_KEY) || "null") || {};
          window.localStorage.setItem(FOOD_THEME_CACHE_KEY, JSON.stringify({ ...current, ...nextConfig }));
        } catch {
          // Continue without persistent storage.
        }
      })
      .catch(() => {
        if (active && !themeConfig && !theme) setThemeConfig({ theme: "current", colors: DEFAULT_THEME_COLORS });
      });
    return () => {
      active = false;
    };
  }, [theme]);

  const resolvedTheme = theme || themeConfig?.theme || "";
  const colors = themeConfig?.colors || DEFAULT_THEME_COLORS;

  useEffect(() => {
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor && resolvedTheme) {
      themeColor.setAttribute("content", resolvedTheme === "delivery_huaraz_red" ? colors.browserBar : "#FFFFFF");
    }
  }, [colors.browserBar, resolvedTheme]);

  if (!resolvedTheme) {
    return <nav className="bottom-nav bottom-nav--loading" aria-label={t("loadingNavigation")} aria-busy="true" />;
  }

  const visibleItems = resolvedTheme === "delivery_huaraz_red" ? deliveryHuarazRedItems : items;
  const navStyle = resolvedTheme === "delivery_huaraz_red" ? {
    "--dh-nav-background": colors.navigationBackground,
    "--dh-nav-active": colors.navigationActive,
    "--dh-nav-inactive": colors.navigationInactive,
  } : undefined;

  return (
    <nav className={`bottom-nav bottom-nav--${resolvedTheme}`} style={navStyle} aria-label={t("mainNavigation")}>
      {visibleItems.map((item) => (
        <NavLink
          to={item.to}
          key={item.to}
          className={({ isActive }) => [
            isActive ? "is-active" : "",
            item.cart && cartPulse ? "cart-just-added" : "",
          ].filter(Boolean).join(" ")}
        >
          <span className="bottom-nav__icon" key={item.cart ? cartPulse : item.to}>
            {item.image ? <img src={item.image} alt="" aria-hidden="true" /> : <MenuIcon name={item.icon} />}
            {item.cart && cartCount > 0 && <em>{cartCount}</em>}
            {item.cart && cartPulse > 0 && <i aria-hidden="true">+1</i>}
          </span>
          <small>{t(item.label)}</small>
        </NavLink>
      ))}
    </nav>
  );
}
